import { makeAutoObservable } from "mobx";
import { IOption } from "../../../components/inputs/select-dropdown/select-dropdown";
import { IAccountAclsModel } from "../../../models/Account";
import { IStoreRole } from "../../../models/User";
import { IChangeable } from "../../../stores/types";
import { C_RMA_ROLES, RolesListStore } from '../stores/role-store';
import { SingleValue } from "react-select";
import AccountEditorStore from "./account-editor-store";
import { PERMISSION } from "../../../models/PermissionsTypes";


/** interface that serves internal store's information only */
interface IModel {
    [id: number]: IStoreRole;
}

interface IConstToRolesMap {
    [key: string]: IStoreRole[] 
}

enum C_RMA_OPTIONS {
    NULL = '__NULL__',
    UNLIMITED = 'UNLIMITED',
    ENFORCED_POLICY_ELIGIBILITY = 'ENFORCED_POLICY_ELIGIBILITY',
}

function cRmaRoleOptionToRolesMap(cRmaRoles: IStoreRole[]): IConstToRolesMap {
    const hasUndefined = (list: (IStoreRole|undefined)[]): boolean => list.some(i => i === undefined);

    const godModeRule = cRmaRoles.find(r => r.name === C_RMA_ROLES.RMA_CREATION_MODE_GOD);
    const policyledRule = cRmaRoles.find(r => r.name === C_RMA_ROLES.RMA_CREATION_MODE_POLICY_LED);

    const result: { [key: string]: (IStoreRole|undefined)[] } = {
        [C_RMA_OPTIONS.NULL]: [],
        [C_RMA_OPTIONS.UNLIMITED]: [godModeRule, policyledRule],
        [C_RMA_OPTIONS.ENFORCED_POLICY_ELIGIBILITY]: [policyledRule],
    }

    Object.keys(result).forEach(key => {
        if (hasUndefined(result[key])) {
            delete result[key];
        }
    });

    /** filter out the null option here if no more options */
    if (Object.keys(result).length === 1 ) {
        delete result[C_RMA_OPTIONS.NULL];
    }

    return result as IConstToRolesMap;
}

function areArraysEqual(arr1: number[], arr2: number[]): boolean {
    arr1 = [ ...arr1 ].sort();
    arr2 = [ ...arr2 ].sort();
    if (arr1.length !== arr2.length) {
        return false;
    }
    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) {
            return false;
        }
    }
    return true;
}

/**
 * This store is reponsible for constructing final array of all Roles that will be applied to account (human user)
 * since we don't have a full Role editor and we cannont from UI add as many roles as we want to an account - only one, 
 * RoleStore contains now 2 exclusive lists of roles, one regular roles and second for C-RMA functionality
 */
class AccountRolesEditor implements IChangeable<IStoreRole[]> {

    private _model: IModel;
    private _changes: IModel;
    
    constructor(private _accountEditor: AccountEditorStore, private _rolesListStore: RolesListStore, accountAcls?: IAccountAclsModel[]) {

        /** construct both _model and _changes from accountAcls, 
         * make sure both objects point to different objects so we will not change _model while changing the _changes 
         * the initial state of both objects should be the same - changes detection mechanism should count on differences between the two */
        this._model = _constructModel(accountAcls);
        this._changes = _constructModel(accountAcls);

        makeAutoObservable(this, {}, { autoBind: true });
    }

    private _constToCRmaRolesMap(): IConstToRolesMap {
        return cRmaRoleOptionToRolesMap(this._rolesListStore.cRmaRoles);
    }

    private get _accountRoleIds(): number[] {
        return Object.values(this._changes).map(acl => acl.id);
    }

    private get _storeRole(): IStoreRole|undefined {
        return this._rolesListStore.storeRoles.filter(r => this._accountRoleIds.includes(r.id))[0];
    }

    public getStoreRoleOptions(): IOption[] {
        return this._rolesListStore.storeRoles.map(r => (
            {
                value: r.id,
                label: r.display_name,
            }
        ));
    }

    public get selectedStoreRole(): IOption|undefined {
        return this.getStoreRoleOptions().find(o => o.value === this._storeRole?.id);
    }

    public setStoreRole(v: SingleValue<IOption>): void {
        /** remove existing store role if exists */
        if (this._storeRole) {
            delete this._changes[this._storeRole.id];
        }

        const selectedRoleId = v?.value;
        const role = this._rolesListStore.storeRoles.find(r => r.id === selectedRoleId);
        if (role === undefined) {
            throw new Error(`Selected Role was not found in store roles list`);
        }

        this._changes[role.id] = role;
    }

    private get _cRmaRoles(): IStoreRole[] {
        return this._rolesListStore.cRmaRoles.filter(r => this._accountRoleIds.includes(r.id));
    }
    
    public getCRmaRolesOptions(): IOption[] {
        const map = this._constToCRmaRolesMap();
        const options: IOption[] = [
            {
                value: C_RMA_OPTIONS.NULL,
                label: 'No Access', 
            },
            {
                value: C_RMA_OPTIONS.UNLIMITED,
                label: 'Configurable',
            },
            {
                value: C_RMA_OPTIONS.ENFORCED_POLICY_ELIGIBILITY,
                label: 'Enforced Policy Eligibility',
            }
        ];

        return Object.keys(map).map(key => options.find(o => o.value === key) as IOption);
    }

    public hasPermissionAll(): boolean {
        let found: boolean = false;
        Object.values(this._changes).forEach((v: IStoreRole) => {
            if (v.permissions?.some(p => p.permission_id === PERMISSION.ALL)) {
                found = true;
            }
        }); 
        return found;
    }

    /** to get the right selected option - compare list of cRma role ids with expected role ids list from option */
    public get selectedCRmaRole(): IOption|undefined {
        const options = this.getCRmaRolesOptions();
        /** special case, when has ALL permission we disable the selector and show configurable option */
        if (this.hasPermissionAll()) {
            return options.find(op => op.value === C_RMA_OPTIONS.UNLIMITED);
        }

        const currentRoles = this._cRmaRoles.map(r => r.id);
        const map = this._constToCRmaRolesMap();

        return options.find(o => {
            const optionRoles = map[o.value].map(r => r.id);
            return areArraysEqual(optionRoles, currentRoles);
        });
    }

    public setCRmaRole(v: SingleValue<IOption>): void { /** TODO:: fix type */
        /** delete all cRma related roles first */
        this._cRmaRoles.forEach(role => {
            delete this._changes[role.id];
        });

        const map = this._constToCRmaRolesMap();
        const roles = map[v?.value as C_RMA_OPTIONS];
        if (roles === undefined) {
            throw new Error(`Selected Role option was not found`);
        }
        
        roles.forEach(role => {
            this._changes[role.id] = role;
        });
    }

    hasChanges(): boolean {
        return JSON.stringify(this._changes) !== JSON.stringify(this._model);
    }

    discardChanges(): void {
        this._changes = JSON.parse(JSON.stringify(this._model));
    }

    getUpdatedModel(): IStoreRole[] {
        return Object.values(this._changes);
    }

}


export default AccountRolesEditor;


function _constructModel(accountAcls: IAccountAclsModel[] = []): IModel {
    /** making sure null is also taken into account */
    const acls = accountAcls ?? [];
    const result: IModel = {};
    acls.forEach(item => {
        result[item.role.id] = item.role;
    });    
    return result;
}
